import { dictFr, dictEn, type DictKey } from "./dict.js";

export type { DictKey };
export { dictFr, dictEn };

export const supportedLocales = ["fr", "en"] as const;
export type Locale = (typeof supportedLocales)[number];
export const defaultLocale: Locale = "fr";

const dicts: Record<Locale, Record<DictKey, string>> = {
  fr: dictFr,
  en: dictEn,
};

export function isLocale(x: string): x is Locale {
  return (supportedLocales as readonly string[]).includes(x);
}

/**
 * Translate a key for a given locale, with optional variable interpolation.
 *
 *   t("seo.model.h1", "fr", { brand: "Ferrari", model: "458" })
 *   → "Ferrari 458 d'occasion"
 *
 * Falls back to the default locale if the key happens to be missing in the
 * requested one (shouldn't happen — TS enforces parity — but defensive).
 */
export function t(
  key: DictKey,
  locale: Locale = defaultLocale,
  vars?: Record<string, string | number>,
): string {
  const raw = dicts[locale]?.[key] ?? dicts[defaultLocale][key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k];
    return v == null ? "" : String(v);
  });
}

/** Bind a locale once, get a curried `t`. Nicer ergonomics inside a render. */
export function tFor(locale: Locale): (key: DictKey, vars?: Record<string, string | number>) => string {
  return (key, vars) => t(key, locale, vars);
}

/**
 * Parse the locale segment from a URL pathname.
 *
 *   "/en/ferrari/458"     → { locale: "en", rest: "/ferrari/458" }
 *   "/ferrari/458"        → { locale: "fr", rest: "/ferrari/458" }   (default)
 *   "/"                   → { locale: "fr", rest: "/" }
 *
 * The default locale ("fr") has NO URL prefix — this is the standard
 * "default-locale-on-root" SEO pattern. Other locales sit under their ISO
 * code.
 */
export function parseLocaleFromPath(pathname: string): { locale: Locale; rest: string } {
  const m = pathname.match(/^\/(\w{2})(?=\/|$)/);
  if (m && isLocale(m[1]!)) {
    const locale = m[1] as Locale;
    if (locale !== defaultLocale) {
      const rest = pathname.slice(3) || "/";
      return { locale, rest };
    }
  }
  return { locale: defaultLocale, rest: pathname };
}

/**
 * Build a locale-aware URL path. For the default locale, returns the path
 * unchanged; for any other locale, prefixes with `/<locale>`.
 */
export function localizePath(path: string, locale: Locale): string {
  if (locale === defaultLocale) return path;
  if (path === "/") return `/${locale}`;
  return `/${locale}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Pick the best locale from an Accept-Language header. Used by the auth
 * email sender to localize the magic-link email based on the request.
 */
export function negotiateLocale(acceptLanguage: string | undefined | null): Locale {
  if (!acceptLanguage) return defaultLocale;
  const parts = acceptLanguage
    .split(",")
    .map((s) => {
      const [tag, qStr] = s.trim().split(";q=");
      const q = qStr ? parseFloat(qStr) : 1;
      return { tag: tag!.toLowerCase(), q: isNaN(q) ? 1 : q };
    })
    .sort((a, b) => b.q - a.q);
  for (const p of parts) {
    const short = p.tag.slice(0, 2);
    if (isLocale(short)) return short;
  }
  return defaultLocale;
}

/**
 * Generate the canonical + every alternate-locale URL for a path. Used by
 * the SEO layout to emit `<link rel="alternate" hreflang="…">` tags and by
 * the sitemap builder.
 *
 * `path` must be the SHARED canonical path WITHOUT a locale prefix, e.g.
 *   "/ferrari/458"  → returns the URLs for /ferrari/458 and /en/ferrari/458.
 */
export function alternatesFor(baseUrl: string, path: string): Array<{ locale: Locale | "x-default"; url: string }> {
  const trimmed = baseUrl.replace(/\/$/, "");
  const list: Array<{ locale: Locale | "x-default"; url: string }> = supportedLocales.map(
    (l) => ({ locale: l, url: `${trimmed}${localizePath(path, l)}` }),
  );
  // x-default points to the default-locale URL.
  list.push({ locale: "x-default", url: `${trimmed}${localizePath(path, defaultLocale)}` });
  return list;
}
